/*
 * Progressive enhancement for product cutout thumbnails.
 * CSS alone cannot read pixel transparency. Only use multiply after a CORS-safe
 * inspection confirms an opaque image with a light, neutral studio background.
 * Transparent images, photographs, and unreadable assets are left untouched.
 */
(() => {
  const targets = [
    '.ls-product-compare__card-media img',
    '.product-card-wrapper .card__media img',
    '.product-v3__quick-package-single-media img',
    '.product-v3__recommendation-media img',
    '.product-v3__upsell-media img',
    '.product-v3__upsell-modal-media img',
    '.ls-cart-item__image',
    '.cart-template-page .cart-item__image',
    '.pi-product-card__media img',
    '.product-card__image img'
  ].join(',');

  const results = new Map();
  const seen = new WeakMap();
  const sampleSize = 64;

  const inspect = (src) => {
    if (results.has(src)) return results.get(src);
    const promise = new Promise((resolve) => {
      const probe = new Image();
      probe.crossOrigin = 'anonymous';
      probe.onload = () => {
        try {
          const canvas = document.createElement('canvas');
          canvas.width = sampleSize;
          canvas.height = sampleSize;
          const context = canvas.getContext('2d', { willReadFrequently: true });
          if (!context) return resolve(false);
          context.clearRect(0, 0, sampleSize, sampleSize);
          context.drawImage(probe, 0, 0, sampleSize, sampleSize);
          const { data } = context.getImageData(0, 0, sampleSize, sampleSize);

          // A single meaningfully transparent pixel means the cutout already
          // blends naturally; never darken it using multiply.
          for (let i = 3; i < data.length; i += 4) {
            if (data[i] < 250) return resolve(false);
          }

          // Do not multiply opaque lifestyle photos or dark backgrounds.
          // Studio thumbnails typically have an almost-white outer border.
          let light = 0;
          let count = 0;
          for (let s = 0; s < sampleSize; s += 8) {
            for (const [x, y] of [[s, 0], [s, sampleSize - 1], [0, s], [sampleSize - 1, s]]) {
              const i = (y * sampleSize + x) * 4;
              const r = data[i], g = data[i + 1], b = data[i + 2];
              if (Math.min(r, g, b) >= 225 && Math.max(r, g, b) - Math.min(r, g, b) <= 18) light++;
              count++;
            }
          }
          resolve(light / count >= 0.9);
        } catch (_) {
          // Some third-party images do not allow canvas access: fail safely.
          resolve(false);
        }
      };
      probe.onerror = () => resolve(false);
      probe.src = src;
    });
    results.set(src, promise);
    return promise;
  };

  const assess = (img) => {
    if (!img.matches(targets)) return;
    const src = img.currentSrc || img.src;
    if (!src || seen.get(img) === src) return;
    seen.set(img, src);
    img.classList.remove('ls-thumb--blend');
    inspect(src).then((canBlend) => {
      if (img.isConnected && (img.currentSrc || img.src) === src && canBlend) {
        img.classList.add('ls-thumb--blend');
      }
    });
  };

  const queue = (img) => {
    if (!img.matches || !img.matches(targets)) return;
    if (!img.dataset.lsThumbBlendListening) {
      img.dataset.lsThumbBlendListening = 'true';
      img.addEventListener('load', () => {
        if (!observer || img.dataset.lsThumbBlendVisible === 'true') assess(img);
      });
    }
    if (observer) observer.observe(img);
    else if (img.complete && img.naturalWidth) assess(img);
  };

  const observer = 'IntersectionObserver' in window
    ? new IntersectionObserver((entries) => {
      entries.forEach(({ isIntersecting, target }) => {
        if (!isIntersecting) return;
        observer.unobserve(target);
        target.dataset.lsThumbBlendVisible = 'true';
        if (target.complete && target.naturalWidth) assess(target);
      });
    }, { rootMargin: '250px' })
    : null;

  const scan = (root) => {
    if (root.nodeType !== 1 && root.nodeType !== 9) return;
    if (root.matches && root.matches(targets)) queue(root);
    root.querySelectorAll(targets).forEach(queue);
  };

  const init = () => {
    scan(document);
    new MutationObserver((mutations) => {
      mutations.forEach(({ addedNodes }) => {
        addedNodes.forEach((node) => scan(node));
      });
    }).observe(document.body, { childList: true, subtree: true });
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init, { once: true });
  else init();
})();